/**
 * ManualInteligencia.jsx · Manual de Inteligencia AlfaNet
 * ────────────────────────────────────────────────────────
 * Documentación cliente-ready de los 6 agentes autónomos (A1-A6).
 * Basado en implementación real de /app/backend/a{1..6}_agent.py.
 */
import { useState } from "react";
import Layout from "../components/Layout";
import {
  Brain, Bot, AlertOctagon, Route, HeartPulse, Target,
  BookOpen, Zap, ArrowRight, Info, Clock, MapPin,
  MessageSquare, Package, Users, Activity, ChevronRight, Sparkles,
} from "lucide-react";

const AGENTS = [
  {
    id: "A1", color: "#0891B2", bg: "#CFFAFE", icon: Brain,
    name: "Agente A1", tagline: "Pre-factibilidad + Stock",
    intro: "Es el portero. Cada OT que llega desde Odoo pasa por A1 antes de entrar al sistema de agendamiento.",
    rules: [
      { name: "Validación de dirección", detail: "Geocodifica la dirección del cliente con Nominatim. Si no es geocodificable, marca la OT como 'sin coordenadas'." },
      { name: "Cobertura geográfica", detail: "Verifica que las coordenadas caigan dentro de un polígono de cobertura activo (tabla zones). Si no, marca 'fuera de cobertura' y notifica al cliente." },
      { name: "Servicio contratado", detail: "Valida que el partner esté en un estado válido (activa, activo, en instalación, por instalar). Bypass para tareas de Infraestructura." },
      { name: "Stock disponible", detail: "Cruza el tipo de solución con su receta de materiales. Verifica bodega del técnico asignado. Semáforo: verde ≥ 100%, naranja ≥ 50%, rojo < 50%." },
      { name: "Capacidad de zona", detail: "Compara agendamientos del día en la zona vs daily_capacity. Si excede, marca 'zona saturada'." },
    ],
    triggers: ["Nueva OT ingresa desde Odoo Live-Sync", "Loop cada 60s reevalúa OTs pendientes", "Manual: botón 'Re-evaluar' en Auditoría A1"],
    output: [
      "OT válida → libera hacia A6 (priorización)",
      "OT bloqueada → marca a1_pending con motivo + envía WhatsApp al cliente",
      "Escalamiento supervisor después de 24h sin resolver",
      "Timeout 48h → deja de reintentar",
    ],
    params: [
      { k: "STOCK_ORANGE_THRESHOLD", v: "50%", desc: "Umbral naranja de disponibilidad de materiales" },
      { k: "STOCK_RED_THRESHOLD", v: "80%", desc: "Falta ≥80% → bloqueo total por stock" },
      { k: "WAIT_CLIENT_HOURS", v: "48h", desc: "Ventana de espera al cliente antes de cerrar" },
      { k: "ESCALATE_SUPERVISOR_HOURS", v: "24h", desc: "Escalamiento a supervisor si sigue pendiente" },
      { k: "LOOP_INTERVAL_SEC", v: "60s", desc: "Frecuencia de re-evaluación de OTs pendientes" },
    ],
  },
  {
    id: "A2", color: "#7C3AED", bg: "#EDE9FE", icon: Bot,
    name: "Agente A2", tagline: "Orquestador de Agendamiento",
    intro: "Es el director de orquesta. Cuando una OT está lista para agendarse, A2 coordina a los demás agentes para encontrar el mejor técnico y horario.",
    rules: [
      { name: "Consulta la cola A6", detail: "Toma OTs ordenadas por score (mayor prioridad primero)." },
      { name: "Pregunta a A3 por zonas bloqueadas", detail: "Descarta OTs cuya zona tiene un evento masivo activo (corte de red, marcha, desastre)." },
      { name: "Consulta a A5 los técnicos disponibles", detail: "Descarta técnicos con imprevisto reportado o breach de SLA activo." },
      { name: "Verifica con A1 el stock del técnico candidato", detail: "Reejecuta la validación de receta contra la bodega del técnico." },
      { name: "Propone slot al cliente vía WhatsApp", detail: "Envía plantilla a2_proposal. El cliente responde '1' para confirmar o '2' para ver alternativas." },
      { name: "Negociación con hasta 3 alternativas", detail: "Si el cliente pide otros horarios, A2 busca 3 slots (A/B/C) en las próximas 48h que no colisionen con la agenda del técnico." },
      { name: "Timeout 2h", detail: "Si el cliente no responde o rechaza todo, la OT pasa a 'Revisión manual' del agendador." },
    ],
    triggers: ["Loop orquestador cada 10 min (en modo LIVE)", "Botón manual 'Dry-Run 8h' para simular", "OT completa validación A1 + entra a la cola A6"],
    output: [
      "Slot confirmado → escribe scheduled_at en smart_ot + notifica al técnico",
      "Sin técnico elegible → mantiene en cola A6 con motivo",
      "Cliente sin respuesta → estado 'Revisión manual' para el agendador humano",
    ],
    params: [
      { k: "NEGOTIATION_TIMEOUT_HOURS", v: "2h", desc: "Tiempo máximo desde primer WhatsApp para respuesta cliente" },
      { k: "ALTERNATIVES_WINDOW_HOURS", v: "48h", desc: "Rango de búsqueda de slots alternativos" },
      { k: "ALTERNATIVES_COUNT", v: "3", desc: "Cantidad de opciones que se ofrecen al cliente (A/B/C)" },
      { k: "MODO ACTUAL", v: "Dry-Run", desc: "Simula sin escribir a DB · pendiente aprobación para LIVE" },
    ],
  },
  {
    id: "A3", color: "#E11D48", bg: "#FFE4E6", icon: AlertOctagon,
    name: "Agente A3", tagline: "Bloqueo de Eventos Masivos",
    intro: "Es el vigía. Cuando ocurre un evento que afecta a una zona entera (corte de energía, protesta, tormenta), A3 bloquea el agendamiento en esa área hasta que se resuelva.",
    rules: [
      { name: "Registro de evento", detail: "Un supervisor o admin crea un evento masivo asociándolo a una zona + rango de fechas + categoría (corte_red, marcha, catástrofe, otro)." },
      { name: "Bloqueo automático de OTs", detail: "Todas las OTs cuyo destino cae dentro del polígono de la zona pasan a estado 'bloqueada_por_evento'." },
      { name: "A2 respeta el bloqueo", detail: "El orquestador excluye esas OTs de propuestas hasta que el evento se cierre." },
      { name: "Cierre del evento", detail: "Al cerrarse (status = closed), las OTs bloqueadas se liberan automáticamente hacia A6." },
      { name: "Trazabilidad completa", detail: "Cada evento guarda: quién lo creó, quién lo cerró, motivo de cierre, cuántas OTs afectó (affected_ot_count)." },
    ],
    triggers: ["Alta manual desde /admin/eventos-masivos", "Umbral: >5 OTs con problema simultáneo en misma zona (futuro)"],
    output: [
      "OTs en zona → estado 'bloqueada_por_evento'",
      "A2 no propone técnicos para esas OTs",
      "Al cerrarse el evento → liberación automática de OTs afectadas",
    ],
    params: [
      { k: "Categorías", v: "5 tipos", desc: "corte_red, marcha, catástrofe, mantenimiento, otro" },
      { k: "Estados", v: "3", desc: "active · closed · expired" },
    ],
  },
  {
    id: "A4", color: "#2563EB", bg: "#DBEAFE", icon: Route,
    name: "Agente A4", tagline: "Enrutamiento GPS · TSP",
    intro: "Es el navegador. Cada mañana calcula la ruta óptima del día para cada técnico usando un algoritmo del vendedor viajero (TSP · nearest-neighbor).",
    rules: [
      { name: "Punto de partida", detail: "Toma la ubicación GPS actual del técnico (último ping) o la bodega asignada." },
      { name: "Compromisos de horario fijos", detail: "OTs con horario comprometido con el cliente se anclan en la ruta dentro de una ventana ±60 min de scheduled_at." },
      { name: "Algoritmo nearest-neighbor", detail: "Ordena las paradas restantes por proximidad. Complejidad O(n²) — óptimo hasta 20 paradas/día." },
      { name: "Estimación realista", detail: "Velocidad promedio 25 km/h × factor tráfico 1.3 = tiempos que reflejan Quito real. Duración por parada: 45 min." },
      { name: "Detección de retrasos", detail: "Compara ETA calculado vs scheduled_at. Si el retraso proyectado ≥ 15 min → dispara plantilla a4_delay al cliente." },
      { name: "Distribución al técnico", detail: "Envía plantilla a4_route por WhatsApp: fecha, total OTs, km, listado ordenado." },
    ],
    triggers: ["Loop diario 05:00 hora Ecuador", "Botón force-recompute en /admin/rutas-a4", "Nueva OT asignada al técnico durante el día"],
    output: [
      "technician_routes ← secuencia ordenada + km + minutos",
      "WhatsApp al técnico con la ruta del día",
      "WhatsApp al cliente si su OT se retrasa >15 min",
    ],
    params: [
      { k: "STOP_MINUTES", v: "45 min", desc: "Duración típica de una OT en sitio" },
      { k: "SPEED_KMH", v: "25 km/h", desc: "Velocidad urbana promedio (calibrado para Quito)" },
      { k: "TRAFFIC_FACTOR", v: "1.3×", desc: "Multiplicador de tiempo por tráfico (heurístico)" },
      { k: "DELAY_THRESHOLD_MIN", v: "15 min", desc: "Umbral para notificar retraso al cliente" },
      { k: "COMMITMENT_WINDOW_MIN", v: "60 min", desc: "Ventana ±min para respetar cita comprometida" },
    ],
  },
  {
    id: "A5", color: "#DB2777", bg: "#FCE7F3", icon: HeartPulse,
    name: "Agente A5", tagline: "Reasignación Dinámica",
    intro: "Es el paramédico. Detecta cuando un técnico no puede seguir con su ruta (enfermó, accidente, sin señal GPS) y reasigna sus OTs al técnico disponible más cercano.",
    rules: [
      { name: "Reporte de imprevisto manual", detail: "El técnico reporta desde la app móvil: enfermedad, falla mecánica, ausencia, accidente, otro." },
      { name: "Detección de silencio GPS", detail: "Si el técnico no envía ping GPS por >60 min durante horario laboral, se alerta y evalúa reasignación." },
      { name: "Detección de SLA breach", detail: "Si una OT lleva >60 min pasada su hora pactada sin arribo del técnico → SLA breach automático." },
      { name: "Reasignación por proximidad", detail: "Busca técnicos disponibles cuya ubicación GPS actual esté a <5km del cliente afectado y tengan capacidad libre." },
      { name: "Notificaciones dobles", detail: "WhatsApp al técnico nuevo (recibe la OT) + WhatsApp al cliente (informa cambio) + interno al agendador." },
    ],
    triggers: ["Loop cada 5 min escanea imprevistos + SLA", "Reporte manual desde app móvil", "Silencio GPS > 60 min en horario laboral"],
    output: [
      "Reescribe technician_email en master_ot + smart_ot",
      "Genera evento en technician_calamities para auditoría",
      "Envía 3 notificaciones WhatsApp (técnico nuevo · cliente · agendador)",
    ],
    params: [
      { k: "GPS_SILENCE_MINUTES", v: "60 min", desc: "Umbral de silencio GPS para alerta" },
      { k: "SLA_BREACH_MINUTES", v: "60 min", desc: "Minutos post-hora pactada para marcar breach" },
      { k: "LOOP_INTERVAL_SEC", v: "300s (5 min)", desc: "Frecuencia del escaneo automático" },
      { k: "Categorías", v: "5", desc: "enfermedad · falla_mecanica · ausencia · accidente · otro" },
    ],
  },
  {
    id: "A6", color: "#7C3AED", bg: "#EDE9FE", icon: Target,
    name: "Agente A6", tagline: "Priorización Dinámica",
    intro: "Es el estratega. Calcula un score para cada OT combinando 4 factores. La cola ordenada por score guía las decisiones de A2 sobre a qué OT atender primero.",
    rules: [
      { name: "SLA restante · peso 40%", detail: "Cuanto menos tiempo queda para vencer el SLA, mayor el score. OTs a horas de vencer suben al top de la cola." },
      { name: "Cliente VIP · peso 25%", detail: "Clientes categorizados como VIP o empresariales obtienen boost. Corporativos y contratos grandes tienen prioridad." },
      { name: "Tipo de solución · peso 20%", detail: "Instalaciones nuevas > mantenimientos preventivos > consultas menores. El tipo de OT afecta el peso." },
      { name: "Tiempo de espera · peso 15%", detail: "OTs que llevan más días sin agendarse ganan score con el tiempo (justicia FIFO)." },
      { name: "Score consolidado", detail: "score = (SLA×40 + VIP×25 + Solución×20 + Espera×15) / 100. Rango 0-100." },
    ],
    triggers: ["Loop cada 3 min recalcula la cola completa", "OT valida A1 (nueva entrada)", "Cambio de scheduled_at o SLA (recálculo forzado)"],
    output: [
      "master_ot.a6_score ← número 0-100",
      "Cola ordenada visible en /admin/cola-a6",
      "A2 consume esta cola en orden descendente",
    ],
    params: [
      { k: "W_SLA", v: "40%", desc: "Peso del componente SLA restante" },
      { k: "W_CLIENT", v: "25%", desc: "Peso del tipo de cliente (VIP boost)" },
      { k: "W_SOLUTION", v: "20%", desc: "Peso del tipo de solución técnica" },
      { k: "W_WAIT", v: "15%", desc: "Peso del tiempo de espera acumulado" },
      { k: "LOOP_INTERVAL_SEC", v: "180s (3 min)", desc: "Frecuencia de recálculo de la cola" },
    ],
  },
];

const GLOSSARY = [
  { term: "SLA", full: "Service Level Agreement", def: "Acuerdo de nivel de servicio. Compromiso de tiempo máximo para atender una OT (ej. 48h para instalaciones VIP)." },
  { term: "TSP", full: "Traveling Salesman Problem", def: "Problema del vendedor viajero. Algoritmo que busca la ruta más corta que visita todos los puntos. A4 usa nearest-neighbor (aproximado, rápido)." },
  { term: "OT", full: "Orden de Trabajo", def: "Tarea individual que un técnico debe ejecutar en campo (instalación, mantenimiento, revisión)." },
  { term: "Polígono de Cobertura", full: "Coverage Polygon", def: "Área geográfica delimitada en el mapa donde AlfaNet ofrece servicio. Definido por lista de puntos [lat,lng] en tabla zones." },
  { term: "Receta de Materiales", full: "Bill of Materials", def: "Lista de ítems necesarios para completar un tipo de solución (ej. Instalación Fibra = 1 ONT + 30m cable + 4 conectores + 1 splitter)." },
  { term: "Slot / Cita", full: "Time slot", def: "Ventana de tiempo asignada a una OT (fecha + hora inicio + hora fin, típicamente 1 hora)." },
  { term: "Dry-Run", full: "Simulación seca", def: "Modo de ejecución donde el agente evalúa y muestra decisiones sin persistir cambios a base de datos. Útil para validar reglas." },
  { term: "Live-Sync", full: "Sincronización en vivo", def: "Consulta directa a la base de datos remota de Odoo en tiempo real. Sin tablas espejo locales." },
  { term: "Score de Prioridad", full: "Priority Score", def: "Número 0-100 asignado por A6 a cada OT. Combina SLA, VIP, tipo de solución y espera." },
  { term: "Imprevisto", full: "Unforeseen event", def: "Evento que impide a un técnico continuar con su ruta (enfermedad, accidente, avería vehículo). Antes llamado 'Calamidad'." },
  { term: "Evento Masivo", full: "Mass Event", def: "Incidencia que afecta a toda una zona (corte de energía, protesta, desastre natural). Manejado por A3." },
  { term: "Nearest-Neighbor", full: "Vecino más cercano", def: "Heurística TSP: en cada paso elige el punto no visitado más cercano al actual. Rápido, ~90% óptimo para <20 paradas." },
  { term: "Ping GPS", full: "GPS heartbeat", def: "Reporte periódico de coordenadas del técnico desde la app móvil (típicamente cada 30-60s en horario laboral)." },
  { term: "Breach de SLA", full: "SLA violation", def: "Cuando una OT excede su tiempo comprometido sin ser atendida. Dispara reasignación en A5." },
  { term: "Cola A6", full: "Priority Queue", def: "Lista ordenada de OTs validadas por A6 esperando ser agendadas. Se consume de mayor a menor score." },
  { term: "Bodega del Técnico", full: "Technician warehouse", def: "Ubicación en Odoo (stock_quant) donde se registra el inventario que porta cada técnico." },
];

export default function ManualInteligencia() {
  return (
    <Layout>
      <ManualInteligenciaContent />
    </Layout>
  );
}

export function ManualInteligenciaContent() {
  const [selectedAgent, setSelectedAgent] = useState(null);

  return (
      <div className="space-y-6" data-testid="manual-page">
        {/* Header · Hero */}
        <div
          className="relative overflow-hidden rounded-2xl p-8"
          style={{
            background: "linear-gradient(135deg, #0A3747 0%, #1E5A7A 50%, #7C3AED 100%)",
          }}
        >
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: "radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}/>
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-3 bg-white/10 backdrop-blur">
              <Sparkles size={11} className="text-yellow-300"/>
              <span className="text-[10px] font-semibold uppercase tracking-widest text-white">Documentación · Cerebro de Agentes</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
              Manual de Inteligencia<br/>
              <span style={{ color: "#DDF247" }}>AlfaNet</span>
            </h1>
            <p className="mt-3 text-base text-white/80 max-w-3xl">
              Detrás del agendamiento hay <b>seis agentes autónomos</b> que colaboran en tiempo real.
              Este manual explica qué hace cada uno, cómo se comunican y por qué toman las decisiones que toman.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {AGENTS.map((a) => (
                <button
                  key={a.id}
                  onClick={() => document.getElementById(`agent-${a.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  data-testid={`nav-${a.id}`}
                  className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white text-xs font-mono transition"
                >
                  {a.id} · {a.tagline}
                </button>
              ))}
              <button
                onClick={() => document.getElementById("glossary")?.scrollIntoView({ behavior: "smooth" })}
                data-testid="nav-glossary"
                className="px-3 py-1.5 rounded-full bg-yellow-400/20 hover:bg-yellow-400/30 backdrop-blur text-yellow-100 text-xs font-mono transition"
              >
                <BookOpen size={11} className="inline mb-0.5"/> Glosario
              </button>
            </div>
          </div>
        </div>

        {/* Ecosistema · Flow Diagram */}
        <section className="neu-card p-6" data-testid="ecosystem">
          <div className="flex items-center gap-2 mb-4">
            <Zap size={16} className="text-purple-700"/>
            <h2 className="text-2xl font-bold" style={{ color: "#0A3747" }}>El Ecosistema · Cómo colaboran</h2>
          </div>
          <p className="text-sm text-gray-600 mb-5 max-w-3xl">
            Cuando una OT nueva llega desde Odoo, atraviesa hasta 6 agentes antes de convertirse en una cita
            confirmada con un técnico. Esta es la ruta típica:
          </p>
          <EcosystemDiagram/>
        </section>

        {/* Matriz de interacción */}
        <section className="neu-card p-6" data-testid="interaction-matrix">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} className="text-purple-700"/>
            <h2 className="text-xl font-bold" style={{ color: "#0A3747" }}>Matriz de Interacciones entre Agentes</h2>
          </div>
          <p className="text-xs text-gray-500 mb-3">Lee: <b>fila</b> le pregunta / notifica a <b>columna</b></p>
          <InteractionMatrix/>
        </section>

        {/* Detalle de cada agente */}
        <div className="space-y-6">
          {AGENTS.map((a) => (
            <AgentSection key={a.id} agent={a}/>
          ))}
        </div>

        {/* Glosario */}
        <section id="glossary" className="neu-card p-6" data-testid="glossary">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen size={16} className="text-purple-700"/>
            <h2 className="text-2xl font-bold" style={{ color: "#0A3747" }}>Glosario de Términos</h2>
          </div>
          <p className="text-sm text-gray-500 mb-5">Definiciones sencillas para usuarios no técnicos.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {GLOSSARY.map((g) => (
              <div key={g.term} className="p-3 rounded-lg border border-gray-200 hover:border-purple-300 transition" data-testid={`glossary-${g.term.replace(/[^a-z0-9]/gi, "").toLowerCase()}`}>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-bold text-base" style={{ color: "#0A3747" }}>{g.term}</span>
                  <span className="text-[10px] font-mono text-purple-700 uppercase tracking-wider">{g.full}</span>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed mt-1">{g.def}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Footer info */}
        <div className="text-center text-[10px] text-gray-400 py-4">
          Manual generado a partir del código real en /app/backend/a[1-6]_agent.py · versión Ago 2026
        </div>
      </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Diagrama de ecosistema (SVG inline)
// ══════════════════════════════════════════════════════════════════════
function EcosystemDiagram() {
  const nodes = [
    { id: "odoo",  label: "Odoo",       x: 60,   y: 100, color: "#94A3B8", desc: "OT ingresa" },
    { id: "A1",    label: "A1",         x: 220,  y: 100, color: "#0891B2", desc: "Pre-factibilidad" },
    { id: "A6",    label: "A6",         x: 380,  y: 100, color: "#7C3AED", desc: "Prioriza" },
    { id: "A2",    label: "A2",         x: 540,  y: 100, color: "#7C3AED", desc: "Orquesta" },
    { id: "A3",    label: "A3",         x: 380,  y: 30,  color: "#E11D48", desc: "Bloqueo eventos" },
    { id: "A5",    label: "A5",         x: 700,  y: 30,  color: "#DB2777", desc: "Reasigna" },
    { id: "WA",    label: "WhatsApp",   x: 700,  y: 100, color: "#25D366", desc: "Negocia con cliente" },
    { id: "smart", label: "smart_ot",   x: 860,  y: 100, color: "#0A3747", desc: "Slot confirmado" },
    { id: "A4",    label: "A4",         x: 860,  y: 200, color: "#2563EB", desc: "Rutea al día siguiente" },
    { id: "tech",  label: "Técnico",    x: 700,  y: 200, color: "#0A3747", desc: "Ejecuta en campo" },
  ];
  const edges = [
    ["odoo", "A1"], ["A1", "A6"], ["A6", "A2"],
    ["A3", "A2"], ["A5", "A2"], ["A2", "WA"], ["WA", "smart"],
    ["smart", "A4"], ["A4", "tech"], ["A5", "tech"],
  ];
  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox="0 0 950 260" className="w-full min-w-[900px]" style={{ maxHeight: 300 }}>
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <path d="M0,0 L8,4 L0,8 Z" fill="#94A3B8"/>
          </marker>
        </defs>
        {/* Edges */}
        {edges.map(([f, t], i) => {
          const a = nodeMap[f], b = nodeMap[t];
          return (
            <line
              key={i}
              x1={a.x + 30} y1={a.y} x2={b.x - 30} y2={b.y}
              stroke="#CBD5E1" strokeWidth="1.5" markerEnd="url(#arrow)"
              strokeDasharray={["A3", "A5"].includes(f) ? "4,3" : ""}
            />
          );
        })}
        {/* Nodes */}
        {nodes.map((n) => (
          <g key={n.id}>
            <circle cx={n.x} cy={n.y} r="28" fill={n.color} opacity="0.15" stroke={n.color} strokeWidth="2"/>
            <text x={n.x} y={n.y + 5} textAnchor="middle" fontSize="14" fontWeight="700" fill={n.color}>
              {n.label}
            </text>
            <text x={n.x} y={n.y + 45} textAnchor="middle" fontSize="10" fill="#64748B">
              {n.desc}
            </text>
          </g>
        ))}
      </svg>
      <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-4">
        <span className="flex items-center gap-1"><span className="inline-block w-4 border-b-2 border-gray-400"></span> Flujo directo</span>
        <span className="flex items-center gap-1"><span className="inline-block w-4 border-b-2 border-gray-400 border-dashed"></span> Consulta lateral</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Matriz de interacciones
// ══════════════════════════════════════════════════════════════════════
function InteractionMatrix() {
  const agents = ["A1", "A2", "A3", "A4", "A5", "A6"];
  const interactions = {
    "A1-A6": "libera OT válida",
    "A2-A1": "revalida stock",
    "A2-A3": "consulta bloqueos",
    "A2-A5": "consulta técnicos disponibles",
    "A2-A6": "consume cola priorizada",
    "A3-A2": "notifica bloqueo → A2 pausa propuestas",
    "A4-A5": "reporta retraso → A5 puede reasignar",
    "A5-A2": "libera OT reasignada",
    "A5-A4": "recalcula ruta del técnico afectado",
    "A6-A2": "publica score",
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left p-2 border-b border-gray-200"></th>
            {agents.map((a) => (
              <th key={a} className="text-center p-2 border-b border-gray-200 font-mono font-bold text-purple-700">{a}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {agents.map((from) => (
            <tr key={from}>
              <td className="p-2 border-b border-gray-100 font-mono font-bold text-purple-700 bg-gray-50">{from}</td>
              {agents.map((to) => {
                const key = `${from}-${to}`;
                const int = interactions[key];
                const same = from === to;
                return (
                  <td key={to} className={`p-2 border-b border-gray-100 text-center ${same ? "bg-gray-100" : ""}`}>
                    {same ? <span className="text-gray-300">—</span> : int ? (
                      <span className="text-[11px] text-gray-700 leading-tight" title={int}>{int}</span>
                    ) : <span className="text-gray-200">·</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Sección de un agente
// ══════════════════════════════════════════════════════════════════════
function AgentSection({ agent }) {
  return (
    <section
      id={`agent-${agent.id}`}
      className="neu-card p-6 scroll-mt-20"
      data-testid={`agent-section-${agent.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{ background: agent.bg }}
        >
          <agent.icon size={30} style={{ color: agent.color }} strokeWidth={2.2}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-widest font-bold" style={{ color: agent.color }}>{agent.id}</div>
          <h3 className="text-2xl font-bold" style={{ color: "#0A3747" }}>{agent.name}</h3>
          <div className="text-sm text-gray-500">{agent.tagline}</div>
        </div>
      </div>

      {/* Intro */}
      <p className="text-base text-gray-700 leading-relaxed mb-5 max-w-3xl">
        <Info size={14} className="inline mr-1 text-purple-500"/> {agent.intro}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Rules */}
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Reglas de Negocio</div>
          <div className="space-y-2">
            {agent.rules.map((r, i) => (
              <div key={i} className="flex gap-2">
                <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white mt-0.5" style={{ background: agent.color }}>
                  {i + 1}
                </div>
                <div>
                  <div className="text-sm font-semibold" style={{ color: "#0A3747" }}>{r.name}</div>
                  <div className="text-xs text-gray-600 leading-relaxed">{r.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Triggers + Output + Params */}
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Disparadores</div>
            <ul className="space-y-1">
              {agent.triggers.map((t, i) => (
                <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                  <Clock size={11} className="text-gray-400 flex-shrink-0 mt-0.5"/>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Resultados</div>
            <ul className="space-y-1">
              {agent.output.map((o, i) => (
                <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                  <ChevronRight size={11} className="flex-shrink-0 mt-0.5" style={{ color: agent.color }}/>
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Params Table */}
      <div className="mt-5 pt-5 border-t border-gray-100">
        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500 mb-2">Parámetros configurables (reales del código)</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-1.5 font-mono font-bold text-gray-500 uppercase text-[10px]">Constante</th>
                <th className="text-left py-1.5 font-mono font-bold text-gray-500 uppercase text-[10px]">Valor</th>
                <th className="text-left py-1.5 font-mono font-bold text-gray-500 uppercase text-[10px]">Descripción</th>
              </tr>
            </thead>
            <tbody>
              {agent.params.map((p) => (
                <tr key={p.k} className="border-b border-gray-50">
                  <td className="py-1.5 font-mono text-[11px]" style={{ color: agent.color }}>{p.k}</td>
                  <td className="py-1.5 font-mono font-bold text-[11px]" style={{ color: "#0A3747" }}>{p.v}</td>
                  <td className="py-1.5 text-[11px] text-gray-600">{p.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
